import React, { createContext, useState, useMemo, useContext, useCallback } from 'react';
import { dataInspectorService } from '../services/DataInspectorService';

export const DataInspectorContext = createContext();

export const DataInspectorProvider = props => {
    const [dataInspector] = useState([]);
    const [groups, setGroups] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [points, setPoints] = useState([]);
    const [functions, setFunctions] = useState([]);
    const [numericPoints, setNumericPoints] = useState([]);

    const getDataInspectorGroups = useCallback(async () => {
        const result = await dataInspectorService.getGroups();
        setGroups(result);
        return result;
    }, []);

    const getDataInspectorBuildings = useCallback(async (groupId, showStale) => {
        const result = await dataInspectorService.getBuildingsByGroupId(groupId, showStale)
        setBuildings(result);
        return result;
    }, []);

    const getDataInspectorEquipment = useCallback(async (body) => {
        const result = await dataInspectorService.getEquipmentByBuildingId(body);
        setEquipment(result);
        return result;
    }, []);

    const getDataInspectorEquipmentByGroup = useCallback(async (body) => {
        const result = await dataInspectorService.getEquipmentByGroupId(body);
        setEquipment(result);
        return result;
    }, []);

    const getDataInspectorEquipmentPoints = useCallback(async (body) => {
        const result = await dataInspectorService.getEquipmentPoints(body);
        setPoints(result);
        return result;
    }, []);

    const getDataInspectorEquipmentFunctions = useCallback(async (body) => {
        const result = await dataInspectorService.getEquipmentFunctions(body);
        setFunctions(result);
        return result;
    }, []);

    const postDataInspectorData = useCallback(async (query) => {
        const result = await dataInspectorService.postDataInspectorQuery(query);
        return result;
    }, []);

    const postDataInspectorExportData = useCallback(async (query) => {
        const result = await dataInspectorService.postDataInspectorExportQuery(query);
        return result;
    }, []);

    const postDataInspectorExportFunctionsData = useCallback(async (query) => {
        const result = await dataInspectorService.postDataInspectorExportFunctionQuery(query);
        return result;
    }, []);

    const updatePointData = useCallback(async (query) => {
        const result = await dataInspectorService.updatePointData(query);
        return result;
    }, []);

    const getFunctionDiagnostics = useCallback(async (query) => {
        const result = await dataInspectorService.getFunctionDiagnostics(query);
        return result;
    }, []);

    const getNumericPoints = useCallback(async (equipmentId, includeChildren) => {
        const result = await dataInspectorService.getNumericPoints(equipmentId, includeChildren);
        setNumericPoints(result);
        return result;
    }, []);

    const createNumericPoint = useCallback(async (equipmentPointId, effectiveDate, value) => {
        return await dataInspectorService.createEquipmentNumericPoint(equipmentPointId, effectiveDate, value);
    }, []);

    const editNumericPoint = useCallback(async (id, effectiveDate, value) => {
        return await dataInspectorService.editEquipmentNumericPoint(id, effectiveDate, value);
    }, []);

    const deleteNumericPoint = useCallback(async (id) => {
        return await dataInspectorService.deleteEquipmentNumericPoint(id);
    }, []);
 
    const values = useMemo(
        () => ({
            groups,
            equipment,
            buildings,
            points,
            functions,
            dataInspector,
            getDataInspectorGroups,
            getDataInspectorBuildings,
            getDataInspectorEquipment,
            getDataInspectorEquipmentByGroup,
            getDataInspectorEquipmentPoints,
            getDataInspectorEquipmentFunctions,
            postDataInspectorData,
            postDataInspectorExportData,
            postDataInspectorExportFunctionsData,
            updatePointData,
            getFunctionDiagnostics,
            getNumericPoints,
            numericPoints,
            createNumericPoint,
            editNumericPoint,
            deleteNumericPoint
        }),
        [
            groups,
            equipment,
            buildings,
            points,
            functions,
            dataInspector,
            getDataInspectorGroups,
            getDataInspectorBuildings,
            getDataInspectorEquipment,
            getDataInspectorEquipmentByGroup,
            getDataInspectorEquipmentPoints,
            getDataInspectorEquipmentFunctions,
            postDataInspectorData,
            postDataInspectorExportData,
            postDataInspectorExportFunctionsData,
            updatePointData,
            getFunctionDiagnostics,
            getNumericPoints,
            numericPoints,
            createNumericPoint,
            editNumericPoint,
            deleteNumericPoint
        ]
    );

    return <DataInspectorContext.Provider value={values}>{props.children}</DataInspectorContext.Provider>;
};

export const useDataInspector = () => {
    
    const context = useContext(DataInspectorContext);

    if (context === undefined) {
        throw new Error('useDataInspector must be used within a DataInspectorProvider component.');
    } 

    return context;
};
